import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Chip,
  Paper,
  Typography,
  Button,
} from '@mui/material';
import { useState, useMemo } from 'react';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { getLeavePeriodDisplay } from '../utils/leavePeriod';
import { utcMs } from '../utils/dateFormat';

export type LeaveDataTableColumn = 'employee' | 'department' | 'leaveType' | 'period' | 'daysTaken' | 'statutory' | 'evidence';

export interface LeaveDataTableEntry {
  employeeId?: string;
  employeeName?: string;
  employeeNumber?: number;
  departmentName?: string;
  leaveTypeName: string;
  daysTaken: number;
  statutoryEntitlementMet: boolean;
  fromDate?: string | null;
  toDate?: string | null;
  periodDescription?: string | null;
  /** 0 = approved request (sorts first), 1 = imported (sorts after). */
  periodSortOrder?: number;
  /** Sort key: approved = fromDate, imported = first day of month. */
  periodSortKey?: string | null;
  leaveRequestId?: string | null;
}

export interface LeaveDataTableProps {
  entries: LeaveDataTableEntry[];
  columns: LeaveDataTableColumn[];
  emptyMessage: string;
  /** When set, table shows pagination. Omit for no pagination. */
  pagination?: {
    page: number;
    rowsPerPage: number;
    onPageChange: (page: number) => void;
    onRowsPerPageChange: (rowsPerPage: number) => void;
    rowsPerPageOptions?: number[];
  };
  /** Tooltip for the Statutory entitlement met header. */
  statutoryHeaderTooltip?: string;
  /** Initial sort field when table first has data. */
  defaultSortField?: LeaveDataTableColumn;
  /** Initial sort direction. */
  defaultSortDirection?: 'asc' | 'desc';
  /** When set, renders the evidence column and calls this for sick leave rows with a request id. */
  onViewSickLeaveEvidence?: (row: LeaveDataTableEntry) => void;
}

function SortableHeader({
  label,
  field,
  sortField,
  sortDirection,
  onSort,
  align,
  tooltip,
}: {
  label: React.ReactNode;
  field: LeaveDataTableColumn;
  sortField: LeaveDataTableColumn | null;
  sortDirection: 'asc' | 'desc';
  onSort: (f: LeaveDataTableColumn) => void;
  align?: 'right';
  tooltip?: string;
}) {
  const icon =
    sortField !== field ? (
      <UnfoldMoreIcon sx={{ fontSize: 16, opacity: 0.5 }} />
    ) : sortDirection === 'asc' ? (
      <ArrowUpwardIcon sx={{ fontSize: 16 }} />
    ) : (
      <ArrowDownwardIcon sx={{ fontSize: 16 }} />
    );
  const content = (
    <Box
      component="span"
      onClick={() => onSort(field)}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover': { opacity: 0.7 },
      }}
    >
      <strong>{label}</strong>
      {icon}
    </Box>
  );
  return (
    <TableCell align={align}>
      {tooltip ? (
        <Tooltip title={tooltip}>{content}</Tooltip>
      ) : (
        content
      )}
    </TableCell>
  );
}

export default function LeaveDataTable({
  entries,
  columns,
  emptyMessage,
  pagination,
  statutoryHeaderTooltip,
  defaultSortField = 'daysTaken',
  defaultSortDirection = 'desc',
  onViewSickLeaveEvidence,
}: LeaveDataTableProps) {
  const [sortField, setSortField] = useState<LeaveDataTableColumn | null>(defaultSortField);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(defaultSortDirection);

  const sortedEntries = useMemo(() => {
    if (entries.length === 0) return [];
    const periodSortKey = (row: LeaveDataTableEntry) => {
      if (row.periodSortKey != null) return row.periodSortKey;
      return row.periodDescription ?? row.fromDate ?? '';
    };
    return [...entries].sort((a, b) => {
      let cmp = 0;
      const field = sortField ?? defaultSortField;
      switch (field) {
        case 'employee':
          cmp = (a.employeeName ?? '').localeCompare(b.employeeName ?? '');
          break;
        case 'department':
          cmp = (a.departmentName ?? '').localeCompare(b.departmentName ?? '');
          break;
        case 'leaveType':
          cmp = (a.leaveTypeName ?? '').localeCompare(b.leaveTypeName ?? '');
          break;
        case 'period': {
          const aOrder = a.periodSortOrder ?? 1;
          const bOrder = b.periodSortOrder ?? 1;
          const dir = sortField ? sortDirection : defaultSortDirection;
          if (aOrder !== bOrder) {
            cmp = dir === 'asc' ? aOrder - bOrder : bOrder - aOrder;
          } else if (a.periodSortKey && b.periodSortKey) {
            const aTime = utcMs(a.periodSortKey);
            const bTime = utcMs(b.periodSortKey);
            if (aTime !== bTime) cmp = dir === 'asc' ? (aTime < bTime ? -1 : 1) : (aTime < bTime ? 1 : -1);
          } else {
            cmp = periodSortKey(a).localeCompare(periodSortKey(b)) || (a.fromDate ?? '').localeCompare(b.fromDate ?? '');
          }
          break;
        }
        case 'daysTaken':
          cmp = a.daysTaken - b.daysTaken;
          break;
        case 'statutory':
          cmp = (a.statutoryEntitlementMet ? 1 : 0) - (b.statutoryEntitlementMet ? 1 : 0);
          break;
        default:
          cmp = a.daysTaken - b.daysTaken;
      }
      const dir = sortField ? sortDirection : defaultSortDirection;
      return dir === 'asc' ? cmp : -cmp;
    });
  }, [entries, sortField, sortDirection, defaultSortField, defaultSortDirection]);

  const displayEntries = useMemo(() => {
    if (!pagination) return sortedEntries;
    const { page, rowsPerPage } = pagination;
    return sortedEntries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [sortedEntries, pagination]);

  const handleSort = (field: LeaveDataTableColumn) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection(field === 'daysTaken' ? 'desc' : 'asc');
    }
    pagination?.onPageChange(0);
  };

  const rowKey = (row: LeaveDataTableEntry, idx: number) =>
    `${row.employeeId ?? ''}-${row.leaveTypeName}-${row.periodDescription ?? row.fromDate ?? idx}`;

  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.includes('employee') && (
                <SortableHeader
                  label="Employee"
                  field="employee"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
              )}
              {columns.includes('department') && (
                <SortableHeader
                  label="Department"
                  field="department"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
              )}
              {columns.includes('leaveType') && (
                <SortableHeader
                  label="Leave type"
                  field="leaveType"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
              )}
              {columns.includes('period') && (
                <SortableHeader
                  label="Period"
                  field="period"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
              )}
              {columns.includes('daysTaken') && (
                <SortableHeader
                  label="Days taken"
                  field="daysTaken"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
              )}
              {columns.includes('statutory') && (
                <SortableHeader
                  label="Statutory entitlement met"
                  field="statutory"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  tooltip={statutoryHeaderTooltip}
                />
              )}
              {columns.includes('evidence') && (
                <TableCell>Sick leave notes</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {displayEntries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 4, border: 'none' }}>
                  <Typography color="text.secondary">{emptyMessage}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              displayEntries.map((row, idx) => (
                <TableRow key={rowKey(row, idx)} hover>
                  {columns.includes('employee') && (
                    <TableCell>
                      {row.employeeName}
                      {row.employeeNumber != null && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          #{row.employeeNumber}
                        </Typography>
                      )}
                    </TableCell>
                  )}
                  {columns.includes('department') && (
                    <TableCell>{row.departmentName ?? '—'}</TableCell>
                  )}
                  {columns.includes('leaveType') && (
                    <TableCell>{row.leaveTypeName}</TableCell>
                  )}
                  {columns.includes('period') && (
                    <TableCell>{getLeavePeriodDisplay(row)}</TableCell>
                  )}
                  {columns.includes('daysTaken') && (
                    <TableCell align="right">{row.daysTaken}</TableCell>
                  )}
                  {columns.includes('statutory') && (
                    <TableCell>
                      {row.statutoryEntitlementMet ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Yes"
                          color="success"
                          size="small"
                          title={statutoryHeaderTooltip}
                        />
                      ) : (
                        <Chip label="No" size="small" variant="outlined" />
                      )}
                    </TableCell>
                  )}
                  {columns.includes('evidence') && (
                    <TableCell>
                      {row.leaveTypeName === 'Sick Leave' && row.leaveRequestId && onViewSickLeaveEvidence ? (
                        <Button
                          size="small"
                          startIcon={<AttachFileIcon />}
                          onClick={() => onViewSickLeaveEvidence(row)}
                        >
                          View notes
                        </Button>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination && (
        <TablePagination
          component="div"
          count={sortedEntries.length}
          page={pagination.page}
          onPageChange={(_, newPage) => pagination.onPageChange(newPage)}
          rowsPerPage={pagination.rowsPerPage}
          onRowsPerPageChange={(e) => {
            pagination.onRowsPerPageChange(parseInt(e.target.value, 10));
            pagination.onPageChange(0);
          }}
          rowsPerPageOptions={pagination.rowsPerPageOptions ?? [10, 25, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
      )}
    </>
  );
}
